import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";
import { ChromePicker } from "react-color";
import type { ColorResult } from "react-color";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import "../styles/PDFPositionTool.css";
import { toast } from "react-toastify";
import { Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { mmToPixels } from "../utils/units";
import { useNavigate } from "react-router-dom";

GlobalWorkerOptions.workerSrc = 
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

interface PDFOption {
  url: string;
  fields: {
    [source: string]: string[];
  };
  firestoreDoc: string;
}

interface FactureZakPositionToolProps {
  pdfOptions: {
    [pdfName: string]: PDFOption;
  };
  defaultPdfName?: string;
}

type FieldData = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  order?: number;
};

type PDFCoordinatesByPage = Record<string, Record<string, FieldData>>;

const FactureZakPositionTool: React.FC<FactureZakPositionToolProps> = ({ pdfOptions, defaultPdfName }) => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hoverCoords, setHoverCoords] = useState<FieldData | null>(null);
  const [coordsByPage, setCoordsByPage] = useState<PDFCoordinatesByPage>({});
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string>(defaultPdfName || "FactureZak.pdf");
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [dragOverField, setDragOverField] = useState<string | null>(null);

  // Définition des champs spécifiques aux factures Zak (basés sur prospects/companies/ZakariaELORCHE/invoices)
  const factureZakFields = {
    "Informations Facture Zak": [
      "id",
      "dateCreation",
      "dateEcheance",
      "nomCompte",
      "mois",
      "montant",
      "montantDhs",
      "createdAt"
    ],
    "Articles": [
      "article1-numero",
      "article1-texte",
      "article1-quantite",
      "article1-prixUnitaire",
      "article1-prixTotal",
      "article2-numero",
      "article2-texte",
      "article2-quantite",
      "article2-prixUnitaire",
      "article2-prixTotal",
      "article3-numero",
      "article3-texte",
      "article3-quantite",
      "article3-prixUnitaire",
      "article3-prixTotal",
      "article4-numero",
      "article4-texte",
      "article4-quantite",
      "article4-prixUnitaire",
      "article4-prixTotal",
      "article5-numero",
      "article5-texte",
      "article5-quantite",
      "article5-prixUnitaire",
      "article5-prixTotal"
    ],
    "Montants Totaux": [
      "montantTotalEUR",
      "montantTotalDHS"
    ]
  };

  // Fonction pour dessiner les labels sur le canvas
  const drawLabels = useCallback((ctx: CanvasRenderingContext2D, height: number, currentCoordsByPage: PDFCoordinatesByPage, currentPage: number) => {
    const pageCoords = currentCoordsByPage[currentPage];
    if (!pageCoords) {
      console.log("Aucune coordonnée pour la page", currentPage);
      return;
    }

    console.log("Dessin des labels pour la page", currentPage, "avec", Object.keys(pageCoords).length, "champs");
    const yOffsetPixels = mmToPixels(Y_OFFSET_CORRECTION);

    Object.entries(pageCoords).forEach(([key, { x, y, size = 12, color, bold, italic }]) => {
      const adjustedY = height - y - yOffsetPixels;
      
      console.log(`Dessin du champ "${key}" à la position (${x}, ${adjustedY})`);
      
      ctx.save();
      ctx.font = `${bold ? 'bold' : 'normal'} ${italic ? 'italic' : 'normal'} ${size}px Arial`;
      ctx.fillStyle = color || "#cc0077";
      ctx.fillText(key, x, adjustedY);
      ctx.restore();
    });
  }, []);

  // Fonction pour rendre une page PDF
  const renderPage = useCallback(async (pageNumber: number) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const viewport = page.getViewport({ scale: 1.5 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Annuler le rendu précédent seulement si nécessaire
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (error) {
          console.log("Rendu précédent déjà annulé");
        }
      }

      renderTaskRef.current = page.render({
        canvasContext: ctx,
        viewport: viewport
      });

      await renderTaskRef.current.promise;
      
      // Dessiner les labels après le rendu du PDF
      drawLabels(ctx, viewport.height, coordsByPage, pageNumber);
      
    } catch (error: any) {
      if (error.message !== "Rendering cancelled") {
        console.error('Erreur lors du rendu de la page:', error);
      }
    }
  }, [pdfDoc, coordsByPage, drawLabels]);

  // Effet pour re-rendre quand les coordonnées changent
  useEffect(() => {
    if (pdfDoc && currentPage) {
      console.log("Re-rendu de la page", currentPage, "avec coordonnées:", coordsByPage);
      const timeoutId = setTimeout(() => {
        renderPage(currentPage);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [pdfDoc, currentPage, coordsByPage, renderPage]);

  // Effet pour l'authentification
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserEmail(user?.email || null);
    });

    return () => unsubscribe();
  }, []);

  // Charger automatiquement le PDF factureZAK.pdf au démarrage
  useEffect(() => {
    const loadInitialPdf = async () => {
      if (selectedPdf && pdfOptions[selectedPdf]) {
        const pdfOption = pdfOptions[selectedPdf];
        try {
          console.log("Chargement automatique du PDF:", selectedPdf);
          const pdf = await getDocument(pdfOption.url).promise;
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setCurrentPage(1);

          // Charger les coordonnées existantes
          const docRef = doc(db, "Coordinates", pdfOption.firestoreDoc);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const loadedCoords = data.coordinates || {};
            
            // Initialiser l'ordre pour les champs qui n'en ont pas
            const coordsWithOrder: PDFCoordinatesByPage = {};
            Object.keys(loadedCoords).forEach(pageNum => {
              const pageCoords = loadedCoords[pageNum] || {};
              const fields = Object.entries(pageCoords);
              coordsWithOrder[pageNum] = {};
              fields.forEach(([key, fieldData], index) => {
                const data = fieldData as FieldData;
                coordsWithOrder[pageNum][key] = {
                  x: data.x,
                  y: data.y,
                  size: data.size,
                  color: data.color,
                  bold: data.bold,
                  italic: data.italic,
                  order: data.order !== undefined ? data.order : index
                };
              });
            });
            
            setCoordsByPage(coordsWithOrder);
            console.log("Coordonnées chargées:", coordsWithOrder);
          } else {
            setCoordsByPage({});
            console.log("Aucune coordonnée existante");
          }
        } catch (error) {
          console.error('Erreur lors du chargement du PDF:', error);
          toast.error('Erreur lors du chargement du PDF');
        }
      }
    };

    loadInitialPdf();
  }, [selectedPdf, pdfOptions]);

  // Fonction pour changer de PDF
  const handlePdfChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const pdfName = e.target.value;
    if (!pdfName) return;

    setSelectedPdf(pdfName);
    const pdfOption = pdfOptions[pdfName];
    if (!pdfOption) return;

    try {
      const pdf = await getDocument(pdfOption.url).promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);

      // Charger les coordonnées existantes
      const docRef = doc(db, "Coordinates", pdfOption.firestoreDoc);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCoordsByPage(data.coordinates || {});
      } else {
        setCoordsByPage({});
      }
    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
      toast.error('Erreur lors du chargement du PDF');
    }
  };

  // Fonction pour capturer les coordonnées de la souris
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setHoverCoords({ x: Math.round(x), y: Math.round(y) });
  }, []);

  // Fonction pour capturer le clic sur le canvas
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setHoverCoords({ x: Math.round(x), y: Math.round(y) });
  }, []);

  // Fonction pour mettre à jour une coordonnée
  const updateCoord = useCallback((fieldName: string, property: keyof FieldData, value: any) => {
    setCoordsByPage(prev => ({
      ...prev,
      [currentPage]: {
        ...prev[currentPage],
        [fieldName]: {
          ...prev[currentPage]?.[fieldName],
          [property]: value
        }
      }
    }));
  }, [currentPage]);

  // Fonction pour supprimer un champ
  const removeField = useCallback((fieldName: string) => {
    setCoordsByPage(prev => {
      const newCoords = { ...prev };
      if (newCoords[currentPage]) {
        delete newCoords[currentPage][fieldName];
      }
      return newCoords;
    });
  }, [currentPage]);

  // Fonction pour dupliquer un champ
  const duplicateField = useCallback((fieldName: string) => {
    setCoordsByPage(prev => {
      const currentPageCoords = prev[currentPage];
      if (!currentPageCoords || !currentPageCoords[fieldName]) return prev;

      const originalField = currentPageCoords[fieldName];
      const fieldCount = Object.keys(currentPageCoords).length;
      
      // Générer un nom unique pour le champ dupliqué
      let newFieldName = `${fieldName}_copy`;
      let counter = 1;
      while (currentPageCoords[newFieldName]) {
        newFieldName = `${fieldName}_copy${counter}`;
        counter++;
      }

      // Dupliquer le champ avec toutes ses propriétés
      const duplicatedField: FieldData = {
        ...originalField,
        order: fieldCount // Nouvel ordre à la fin
      };

      return {
        ...prev,
        [currentPage]: {
          ...currentPageCoords,
          [newFieldName]: duplicatedField
        }
      };
    });
  }, [currentPage]);

  // Gestionnaires pour le drag & drop
  const handleDragStart = useCallback((e: React.DragEvent, fieldName: string) => {
    setDraggedField(fieldName);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', fieldName);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, fieldName: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedField && draggedField !== fieldName) {
      setDragOverField(fieldName);
    }
  }, [draggedField]);

  const handleDragLeave = useCallback(() => {
    setDragOverField(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetFieldName: string) => {
    e.preventDefault();
    setDragOverField(null);

    if (!draggedField || draggedField === targetFieldName) {
      setDraggedField(null);
      return;
    }

    setCoordsByPage(prev => {
      const currentPageCoords = prev[currentPage];
      if (!currentPageCoords) return prev;

      const fields = Object.entries(currentPageCoords)
        .sort(([, a], [, b]) => {
          const orderA = a.order !== undefined ? a.order : 999999;
          const orderB = b.order !== undefined ? b.order : 999999;
          return orderA - orderB;
        });

      const draggedIndex = fields.findIndex(([key]) => key === draggedField);
      const targetIndex = fields.findIndex(([key]) => key === targetFieldName);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedField(null);
        return prev;
      }

      // Réordonner les champs
      const [removed] = fields.splice(draggedIndex, 1);
      fields.splice(targetIndex, 0, removed);

      // Mettre à jour les ordres
      const newCoords: Record<string, FieldData> = {};
      fields.forEach(([key, data], index) => {
        newCoords[key] = { ...data, order: index };
      });

      setDraggedField(null);
      return {
        ...prev,
        [currentPage]: newCoords
      };
    });
  }, [draggedField, currentPage]);

  // Fonction pour ajouter un champ depuis les coordonnées de survol
  const addFieldFromHover = useCallback(() => {
    if (!hoverCoords || !selectedField) {
      console.log("Impossible d'ajouter le champ:", { hoverCoords, selectedField });
      return;
    }

    console.log("Ajout du champ", selectedField, "aux coordonnées", hoverCoords);

    setCoordsByPage(prev => {
      const currentPageCoords = prev[currentPage] || {};
      const fieldCount = Object.keys(currentPageCoords).length;
      
      const newCoords = {
        ...prev,
        [currentPage]: {
          ...prev[currentPage],
          [selectedField]: {
            ...hoverCoords,
            size: 12,
            color: "#000000",
            bold: false,
            italic: false,
            order: fieldCount // Initialiser l'ordre avec le nombre de champs existants
          }
        }
      };
      
      console.log("Nouvelles coordonnées:", newCoords);
      
      // Redessiner les labels immédiatement
      setTimeout(() => {
        if (canvasRef.current && pdfDoc) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const viewport = canvas.getBoundingClientRect();
            drawLabels(ctx, viewport.height, newCoords, currentPage);
          }
        }
      }, 50);
      
      return newCoords;
    });

    setSelectedField(null);
  }, [hoverCoords, selectedField, currentPage, pdfDoc, drawLabels]);

  // Fonction pour changer la couleur
  const handleColorChange = useCallback((fieldName: string, color: ColorResult) => {
    updateCoord(fieldName, "color", color.hex);
  }, [updateCoord]);

  // Fonction pour ajouter un champ de test
  const addTestField = () => {
    const testCoords = {
      x: 100,
      y: 100,
      size: 16,
      color: "#ff0000",
      bold: true,
      italic: false
    };

    setCoordsByPage(prev => ({
      ...prev,
      [currentPage]: {
        ...prev[currentPage],
        "TEST_CHAMP": testCoords
      }
    }));

    console.log("Champ de test ajouté aux coordonnées:", testCoords);
  };

  // Fonction pour tester la connexion et créer un document de test
  const testFirestoreConnection = async () => {
    console.log("Test de connexion Firestore...");
    console.log("User email:", userEmail);
    
    if (!userEmail) {
      toast.error("Vous devez être connecté pour tester");
      return;
    }

    try {
      const testDocRef = doc(db, "Coordinates", "testConnection");
      await setDoc(testDocRef, {
        test: true,
        timestamp: serverTimestamp(),
        user: userEmail
      });
      
      console.log("Test de connexion réussi !");
      toast.success("Connexion Firestore OK !");
    } catch (error: any) {
      console.error("Erreur de connexion Firestore:", error);
      toast.error(`Erreur de connexion: ${error.message}`);
    }
  };

  // Fonction pour sauvegarder les coordonnées
  const saveCoordinatesToFirestore = async (coordinates: PDFCoordinatesByPage) => {
    console.log("Tentative de sauvegarde des coordonnées...");
    console.log("User email:", userEmail);
    console.log("Selected PDF:", selectedPdf);
    console.log("Coordinates to save:", coordinates);

    if (!userEmail) {
      toast.error("Vous devez être connecté pour sauvegarder");
      console.log("Erreur: Utilisateur non connecté");
      return;
    }

    const pdfOption = pdfOptions[selectedPdf];
    if (!pdfOption) {
      toast.error("PDF non sélectionné");
      console.log("Erreur: PDF non sélectionné");
      return;
    }

    console.log("Document Firestore à utiliser:", pdfOption.firestoreDoc);

    setIsSaving(true);
    try {
      const docRef = doc(db, "Coordinates", pdfOption.firestoreDoc);
      console.log("Référence du document:", docRef.path);
      
      const dataToSave = {
        coordinates,
        updatedBy: userEmail,
        updatedAt: serverTimestamp(),
        pdfName: selectedPdf
      };
      
      console.log("Données à sauvegarder:", dataToSave);
      
      await setDoc(docRef, dataToSave);

      console.log("Sauvegarde réussie !");
      toast.success("Coordonnées sauvegardées avec succès !");
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(`Erreur lors de la sauvegarde: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Génération des groupes de champs pour le select
  const fieldGroups = Object.entries(factureZakFields).map(([source, fields]) => (
    <optgroup key={source} label={source}>
      {fields.map(field => (
        <option key={`${source}-${field}`} value={field}>
          {field}
        </option>
      ))}
    </optgroup>
  ));

  return (
    <div className="pdf-position-tool-container" style={{ paddingTop: '80px' }}>
      <h2>Outil de Repérage Facture Zak</h2>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <select
          value={selectedPdf}
          onChange={handlePdfChange}
          className="pdf-rep-select"
        >
          <option value="">Sélectionnez un type de document</option>
          <option value="FactureZak.pdf">Facture Zak</option>
        </select>
      </div>
      <p className="pdf-instruction">
        Cliquez sur le PDF pour obtenir les coordonnées <code>X / Y</code> et modifiez-les à droite.
      </p>

      <div className="pdf-body">
        <div className="pdf-viewer-container">
          <canvas
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onClick={handleCanvasClick}
            className="pdf-canvas"
          />
          {totalPages > 1 && (
            <div className="pdf-navigation">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Précédent
              </button>
              <span>Page {currentPage} / {totalPages}</span>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Suivant →
              </button>
            </div>
          )}
        </div>

        <div className="pdf-coord-fields">
          <div className="coord-info">
            <h3>Coordonnées actuelles</h3>
            {hoverCoords && (
              <p>
                <strong>X:</strong> {hoverCoords.x} | <strong>Y:</strong> {hoverCoords.y}
              </p>
            )}
          </div>

          <div className="coord-table-container">
            <table className="coord-table">
              <thead>
                <tr>
                  <th>Champ</th>
                  <th>X</th>
                  <th>Y</th>
                  <th>Taille</th>
                  <th>Couleur</th>
                  <th>Gras</th>
                  <th>Italique</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {coordsByPage[currentPage] && 
                  Object.entries(coordsByPage[currentPage])
                    .sort(([, a], [, b]) => {
                      // Trier par ordre si défini, sinon par ordre d'ajout
                      const orderA = a.order !== undefined ? a.order : 999999;
                      const orderB = b.order !== undefined ? b.order : 999999;
                      return orderA - orderB;
                    })
                    .map(([key, { x, y, size, color, bold, italic }], index, array) => (
                    <tr 
                      key={key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, key)}
                      onDragOver={(e) => handleDragOver(e, key)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, key)}
                      style={{
                        cursor: 'move',
                        backgroundColor: dragOverField === key ? '#e3f2fd' : draggedField === key ? '#fff3e0' : 'transparent',
                        opacity: draggedField === key ? 0.5 : 1,
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <td style={{ cursor: 'grab' }}>{key}</td>
                      <td>
                        <input
                          type="number"
                          value={x}
                          onChange={(e) => updateCoord(key, "x", parseInt(e.target.value) || 0)}
                          placeholder="X"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={y}
                          onChange={(e) => updateCoord(key, "y", parseInt(e.target.value) || 0)}
                          placeholder="Y"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={size ?? 12}
                          onChange={(e) => updateCoord(key, "size", parseInt(e.target.value) || 12)}
                          placeholder="Taille"
                        />
                      </td>
                      <td>
                        <div className="color-picker-container">
                          <div
                            className="color-preview"
                            style={{ backgroundColor: color || "#cc0077" }}
                            onClick={() => setColorPickerOpen(key)}
                          />
                          {colorPickerOpen === key && (
                            <div className="color-popover">
                              <div
                                className="color-cover"
                                onClick={() => setColorPickerOpen(null)}
                              />
                              <ChromePicker
                                color={color || "#cc0077"}
                                onChangeComplete={(color) => handleColorChange(key, color)}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!bold}
                            onChange={(e) => updateCoord(key, "bold", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                         <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!italic}
                            onChange={(e) => updateCoord(key, "italic", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <button
                            onClick={() => duplicateField(key)}
                            title="Dupliquer ce champ"
                            onMouseDown={(e) => e.stopPropagation()} // Empêcher le drag lors du clic sur le bouton
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                          >
                            <i className="bi bi-files" style={{ fontSize: '12px' }}></i>
                          </button>
                          <button
                            className="remove-field-btn"
                            onClick={() => removeField(key)}
                            title="Supprimer ce champ"
                            onMouseDown={(e) => e.stopPropagation()} // Empêcher le drag lors du clic sur le bouton
                          >
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
    
            <div className="pdf-field-adder">
              <select
                value={selectedField || ""}
                onChange={(e) => setSelectedField(e.target.value || null)}
              >
                <option value="">-- Ajouter un champ --</option>
                {fieldGroups}
              </select>
              
              <button
                onClick={addFieldFromHover}
                disabled={!hoverCoords || !selectedField}
              >
                ➕ {selectedField ? `Ajouter/mettre à jour ${selectedField}` : "Ajouter un champ"}
              </button>
              
              <div className="pdf-actions">
                <button 
                  onClick={addTestField}
                  className="test-button"
                  style={{ backgroundColor: "#ff6b6b", marginRight: "10px" }}
                >
                  🧪 Ajouter champ de test
                </button>
                
                <button 
                  onClick={testFirestoreConnection}
                  className="test-connection-button"
                  style={{ backgroundColor: "#4ecdc4", marginRight: "10px" }}
                >
                  🔗 Test connexion
                </button>
                
                <button 
                  onClick={() => saveCoordinatesToFirestore(coordsByPage)}
                  disabled={isSaving || !userEmail}
                  className="save-button"
                >
                  {isSaving ? "Enregistrement..." : "Enregistrer dans Firestore"}
                </button>
                
                {!userEmail && (
                  <p className="auth-warning">
                    Connectez-vous pour activer l'enregistrement
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactureZakPositionTool;
